import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
} from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { NotificationTemplateApi } from '@shared/sdk-v1/apis/NotificationTemplateApi';
import type {
  NotificationTemplate,
  NotificationTemplateBuiltInPreview,
  NotificationTemplateSendTestRequest,
  NotificationTemplateSendTestResult,
} from '@shared/sdk-v1/models/NotificationTemplate';
import { apiConfig } from '../shared/api-config';
import { getCourierRealm } from './courierSlice';

export interface TemplateDefault {
  event: string;
  channel: string;
  subject: string | null;
  body: string;
}

interface NotificationTemplatesState {
  templates: NotificationTemplate[];
  defaults: TemplateDefault[];
  /** Realm the loaded `templates` belong to — see FetchArg below. */
  templatesRealm: string | null;
  isLoading: boolean;
  isSaving: boolean;
  isSendingTest: boolean;
  error: string | null;
  lastTestResult: NotificationTemplateSendTestResult | null;
  /**
   * The built-in message a customer gets when the courier keeps the default,
   * keyed `${realm}::${event}::${channel}` — the realm is in the key so an
   * impersonation switch can never show another courier's branding.
   */
  builtInPreviews: Record<string, NotificationTemplateBuiltInPreview>;
}

const initialState: NotificationTemplatesState = {
  templates: [],
  defaults: [],
  templatesRealm: null,
  isLoading: false,
  isSaving: false,
  isSendingTest: false,
  error: null,
  lastTestResult: null,
  builtInPreviews: {},
};

const previewKey = (realm: string, event: string, channel: string) =>
  `${realm}::${event}::${channel}`;

function apiClient(): NotificationTemplateApi {
  return new NotificationTemplateApi(apiConfig());
}

/**
 * Which courier's templates a request refers to.
 *
 * Admin impersonation is client-side only — the access token never changes —
 * so unless the realm travels with the request the backend scopes everything
 * to the signed-in admin's OWN courier. That is why templates saved while
 * impersonating were tagged with the wrong tenant (and so never fired), and
 * why "Send test" arrived from the wrong courier's mail account.
 *
 * `getCourierRealm` already returns the impersonated courier's realm, because
 * courierSlice overwrites `state.courier` when impersonation starts.
 */
type FetchArg = { realm?: string } | void;

function resolveRealm(
  state: RootState,
  explicit?: string
): string | undefined {
  return explicit || getCourierRealm(state) || undefined;
}

export const fetchNotificationTemplates = createAsyncThunk<
  NotificationTemplate[],
  FetchArg,
  { state: RootState; rejectValue: string }
>(
  'notificationTemplates/fetchAll',
  async (arg, { getState, rejectWithValue }) => {
    const realm = resolveRealm(getState(), arg?.realm);
    if (!realm) {
      // Fetching unscoped would silently show the admin's own templates while
      // they believe they are looking at the impersonated courier's.
      return rejectWithValue('No courier selected — cannot load templates');
    }
    try {
      return await apiClient().notificationTemplateFind({
        filter: JSON.stringify({ where: { realm } }),
      });
    } catch (err) {
      return rejectWithValue(
        err instanceof Error ? err.message : 'Failed to load templates'
      );
    }
  }
);

export const fetchNotificationTemplateDefaults = createAsyncThunk<
  TemplateDefault[],
  void,
  { rejectValue: string }
>(
  'notificationTemplates/fetchDefaults',
  async (_, { rejectWithValue }) => {
    try {
      return await apiClient().notificationTemplateDefaults();
    } catch (err) {
      return rejectWithValue(
        err instanceof Error ? err.message : 'Failed to load defaults'
      );
    }
  }
);

export const saveNotificationTemplate = createAsyncThunk<
  NotificationTemplate,
  Partial<NotificationTemplate> & { id?: string },
  { state: RootState; rejectValue: string }
>(
  'notificationTemplates/save',
  async (input, { getState, rejectWithValue }) => {
    const realm = resolveRealm(getState(), input.realm);
    if (!realm) {
      return rejectWithValue('No courier selected — cannot save template');
    }
    // Sent on the patch as well as the create: it pins the write to the courier
    // the editor is showing, and the backend rejects a realm the caller is not
    // allowed to write to rather than silently retargeting it.
    const data = { ...input, realm };
    try {
      const api = apiClient();
      if (input.id) {
        return await api.notificationTemplatePatch({
          id: input.id,
          data,
        });
      }
      return await api.notificationTemplateCreate({ data });
    } catch (err) {
      return rejectWithValue(
        err instanceof Error ? err.message : 'Failed to save template'
      );
    }
  }
);

export const deleteNotificationTemplate = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('notificationTemplates/delete', async (id, { rejectWithValue }) => {
  try {
    await apiClient().notificationTemplateDelete({ id });
    return id;
  } catch (err) {
    return rejectWithValue(
      err instanceof Error ? err.message : 'Failed to reset template'
    );
  }
});

export const fetchBuiltInPreview = createAsyncThunk<
  { key: string; preview: NotificationTemplateBuiltInPreview },
  { event: string; channel: string; realm?: string },
  { state: RootState; rejectValue: string }
>(
  'notificationTemplates/fetchBuiltInPreview',
  async ({ event, channel, realm: explicit }, { getState, rejectWithValue }) => {
    const realm = resolveRealm(getState(), explicit);
    if (!realm) {
      return rejectWithValue('No courier selected — cannot load the preview');
    }
    try {
      const preview = await apiClient().notificationTemplateBuiltInPreview({
        event,
        channel,
        realm,
      });
      return { key: previewKey(realm, event, channel), preview };
    } catch (err) {
      return rejectWithValue(
        err instanceof Error ? err.message : 'Failed to load the preview'
      );
    }
  }
);

export const sendNotificationTemplateTest = createAsyncThunk<
  NotificationTemplateSendTestResult,
  NotificationTemplateSendTestRequest,
  { state: RootState; rejectValue: string }
>(
  'notificationTemplates/sendTest',
  async (data, { getState, rejectWithValue }) => {
    // Without this the test renders and sends as the signed-in admin's own
    // courier — the "test arrived from OX Packages" bug.
    const realm = resolveRealm(getState(), data.realm);
    try {
      return await apiClient().notificationTemplateSendTest({
        data: realm ? { ...data, realm } : data,
      });
    } catch (err) {
      return rejectWithValue(
        err instanceof Error ? err.message : 'Test send failed'
      );
    }
  }
);

const slice = createSlice({
  name: 'notificationTemplates',
  initialState,
  reducers: {
    clearTestResult: (state) => {
      state.lastTestResult = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationTemplates.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        // Drop the previous courier's rows immediately on an impersonation
        // switch — otherwise they stay on screen (and are editable) until the
        // new request lands, which is how you save into the wrong tenant.
        const incoming = action.meta.arg?.realm;
        if (incoming && incoming !== state.templatesRealm) {
          state.templates = [];
        }
      })
      .addCase(fetchNotificationTemplates.fulfilled, (state, action) => {
        state.templates = action.payload;
        state.templatesRealm = action.meta.arg?.realm ?? state.templatesRealm;
        state.isLoading = false;
      })
      .addCase(fetchNotificationTemplates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to load templates';
      })
      .addCase(
        fetchNotificationTemplateDefaults.fulfilled,
        (state, action: PayloadAction<TemplateDefault[]>) => {
          state.defaults = action.payload;
        }
      )
      .addCase(saveNotificationTemplate.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(
        saveNotificationTemplate.fulfilled,
        (state, action: PayloadAction<NotificationTemplate>) => {
          const idx = state.templates.findIndex(
            (t) => t.id === action.payload.id
          );
          if (idx >= 0) {
            state.templates[idx] = action.payload;
          } else {
            // Replace any existing row for the same (event, channel) pair —
            // the backend uniqueness constraint guarantees only one.
            const dupIdx = state.templates.findIndex(
              (t) =>
                t.event === action.payload.event &&
                t.channel === action.payload.channel
            );
            if (dupIdx >= 0) {
              state.templates[dupIdx] = action.payload;
            } else {
              state.templates.push(action.payload);
            }
          }
          state.isSaving = false;
        }
      )
      .addCase(saveNotificationTemplate.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload || 'Failed to save template';
      })
      .addCase(deleteNotificationTemplate.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(
        deleteNotificationTemplate.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.templates = state.templates.filter(
            (t) => t.id !== action.payload
          );
          state.isSaving = false;
        }
      )
      .addCase(deleteNotificationTemplate.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload || 'Failed to reset template';
      })
      .addCase(fetchBuiltInPreview.fulfilled, (state, action) => {
        state.builtInPreviews[action.payload.key] = action.payload.preview;
      })
      .addCase(sendNotificationTemplateTest.pending, (state) => {
        state.isSendingTest = true;
        state.error = null;
        state.lastTestResult = null;
      })
      .addCase(
        sendNotificationTemplateTest.fulfilled,
        (
          state,
          action: PayloadAction<NotificationTemplateSendTestResult>
        ) => {
          state.lastTestResult = action.payload;
          state.isSendingTest = false;
        }
      )
      .addCase(sendNotificationTemplateTest.rejected, (state, action) => {
        state.isSendingTest = false;
        state.error = action.payload || 'Test send failed';
      });
  },
});

export const { clearTestResult, clearError } = slice.actions;

// Selectors
export const selectTemplates = (state: RootState) =>
  state.notificationTemplates?.templates || [];
export const selectTemplatesRealm = (state: RootState) =>
  state.notificationTemplates?.templatesRealm || null;
export const selectTemplateDefaults = (state: RootState) =>
  state.notificationTemplates?.defaults || [];
export const selectDefaultFor = (event: string, channel: string) => (
  state: RootState
): TemplateDefault | undefined =>
  (state.notificationTemplates?.defaults || []).find(
    (d) => d.event === event && d.channel === channel
  );
export const selectTemplateFor = (event: string, channel: string) => (
  state: RootState
): NotificationTemplate | undefined =>
  (state.notificationTemplates?.templates || []).find(
    (t) => t.event === event && t.channel === channel
  );
export const selectBuiltInPreview = (
  realm: string | undefined,
  event: string,
  channel: string
) => (state: RootState): NotificationTemplateBuiltInPreview | undefined =>
  realm
    ? state.notificationTemplates?.builtInPreviews?.[
        previewKey(realm, event, channel)
      ]
    : undefined;
export const selectIsLoading = (state: RootState) =>
  state.notificationTemplates?.isLoading || false;
export const selectIsSaving = (state: RootState) =>
  state.notificationTemplates?.isSaving || false;
export const selectIsSendingTest = (state: RootState) =>
  state.notificationTemplates?.isSendingTest || false;
export const selectLastTestResult = (state: RootState) =>
  state.notificationTemplates?.lastTestResult || null;
export const selectError = (state: RootState) =>
  state.notificationTemplates?.error || null;

export default slice.reducer;
